import {
  ArrayUtils,
  DateTimeUtils,
  EquipmentOverviewResponseDto,
  NumberUtils,
  PersonAssignedToOperationDto,
  SapOperationDto,
  SapPriorityType,
  SapStatusDto,
  StringUtils,
  WebPubSubResponse
} from '@festo-ax/smartenance-shared-ui';
import { SapMachineOverviewVM, SapOrderOperationVM } from '../../models/sap';
import { SapAssignedPerson } from '../../models/reducers/component-store';
import { MetaData } from '../../models/reducers/meta-data';
import { MetaDataReducerUtils } from './meta-data.utils';

export class SapTypesMapperUtils {
  static convertStringToPriority(value: string): SapPriorityType | null {
    if (!StringUtils.isString(value) || value.length === 0) {
      return null;
    }
    // please provide all possible types here
    const isMatch = (value: string): value is SapPriorityType => ['1', '2', '3', 'T'].includes(value);

    return isMatch(value) ? value : null;
  }

  // TODO maybe we need not a string[], but some object here?
  static convertStatusDto(value: SapStatusDto[] | undefined): string[] {
    if (ArrayUtils.isEmpty(value)) {
      return [];
    }

    return value.map((status: SapStatusDto) => status.status);
  }

  static convertSapTimeStringToDate(value: string): Date | null {
    if (typeof value !== 'string') {
      return null;
    }

    return DateTimeUtils.extractTime(value);
  }

  static convertSapDateStringToDate(value: string): Date | null {
    if (typeof value !== 'string') {
      return null;
    }

    const date: number | null = NumberUtils.extractNumberFromString(value);

    return date ? new Date(NumberUtils.integerForDate(date)) : null;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static convertResponseMetaToMetaData(meta: MetaData, response: WebPubSubResponse<any>): MetaData {
    const totalAmount: number = response?.data?.result?.totalAmount || 0;
    const offset: number = response?.data?.result?.offset || 0;

    return MetaDataReducerUtils.updatePagination(meta, totalAmount, offset);
  }

  static mapSapOperationDtosToSapOrderOperationsVM(dtos: SapOperationDto[]): SapOrderOperationVM[] {
    if (ArrayUtils.isEmpty(dtos)) {
      return [];
    }

    return dtos.map(this.mapSapOperationDtoToSapOrderOperationVM.bind(this));
  }

  static mapSapOperationDtoToSapOrderOperationVM(dto: SapOperationDto): SapOrderOperationVM {
    const startTime: Date | null = DateTimeUtils.extractTime(dto.startTime);
    const startDate: Date = startTime
      ? DateTimeUtils.convertDateTimeToDate(this.convertSapDateStringToDate(dto.startDate), startTime)
      : this.convertSapDateStringToDate(dto.startDate);
    const endTime: Date | null = DateTimeUtils.extractTime(dto.endTime);
    const endDate: Date = endTime
      ? DateTimeUtils.convertDateTimeToDate(this.convertSapDateStringToDate(dto.endDate), endTime)
      : this.convertSapDateStringToDate(dto.endDate);

    return {
      startDate,
      endDate,
      operationId: dto?.operationId || null,
      orderId: dto?.orderId || null,
      priority: this.convertStringToPriority(dto?.priority),
      machine: {
        number: dto?.machine?.number || null,
        label: dto?.machine?.label || null,
      },
      orderType: {
        code: dto?.orderType?.code || null,
        description: dto?.orderType?.description || null,
      },
      assetLocation: null, // TODO remove or find a place were to take
      description: dto?.description || null,
      title: dto?.title || null,
      systemState: this.convertStatusDto(dto?.systemStatus),
      userState: this.convertStatusDto(dto?.userStatus),
      assignedPerson: this.mapOperationAssignedPersonsModelToSapAssignedPerson(dto?.assignedPerson),
      activityType: {
        code: dto?.activityType?.code || null,
        description: dto?.activityType?.description || null
      },
      workCenter: {
        code: dto?.workCenter?.code || null,
        description: dto?.workCenter?.description || null
      },
      functionalLocation: {
        code: dto?.functionalLocation?.code || null,
        description: dto?.functionalLocation?.description || null
      },
      objectNumber: dto?.objectNumber || null,
      plant: dto?.plant || null,
      isConfirmed: dto?.isConfirmed
    };
  }

  static mapOperationAssignedPersonsModelToSapAssignedPerson(dtos: PersonAssignedToOperationDto[]): SapAssignedPerson[] {
    if (ArrayUtils.isEmpty(dtos)) {
      return [];
    }

    return dtos.map(this.mapOperationAssignedPersonToSapAssignedPerson.bind(this));
  }

  static mapOperationAssignedPersonToSapAssignedPerson(dto: PersonAssignedToOperationDto): SapAssignedPerson {
    return {
      personId: dto.personId,
      firstName: dto.firstName,
      lastName: dto.lastName,
      activity: dto.activity,
      orderId: dto.orderId,
    }
  }

  static mapEquipmentOverviewResponseDtoArrayToSapMachineOverviewVMArray(dtos: EquipmentOverviewResponseDto[]): SapMachineOverviewVM[] {
    if (ArrayUtils.isEmpty(dtos)) {
      return [];
    }

    return dtos.map((dto: EquipmentOverviewResponseDto) => this.mapEquipmentOverviewResponseDtoToSapMachineOverviewVM(dto));
  }

  static mapEquipmentOverviewResponseDtoToSapMachineOverviewVM(dto: EquipmentOverviewResponseDto): SapMachineOverviewVM {
    return {
      id: dto?.id || null,
      number: dto?.number || null,
      name: dto?.name || null,
      createdAt: dto?.createdAt ? new Date(dto.createdAt) : null,
      externalReferenceUrl: dto?.externalReferenceUrl || null,
      media: null,
    }
  }
}
